import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Pagination,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import routes from 'constants/routes';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { getAgencyIdOrDefault } from 'features/auth/constants';
import { getInvoicesByPassenger, getPassengerInvoiceSummary } from 'features/invoices/services';
import { IInvoiceSummary, InvoiceStatusEnum, IPassengerInvoiceSummary } from 'features/invoices/types';
import { formatAmount, formatDate, pendingAmountInMainCurrency, TYPE_LABEL } from 'features/invoices/uiHelpers';

/**
 * Estado de cuenta del cliente: resumen de saldos (calculado por la API,
 * escala a cualquier volumen) + documentos emitidos paginados, contra
 * GET /passengers/invoices/{passengerId}.
 */

const PAGE_SIZE = 20;

interface PassengerAccountStatementProps {
	open: boolean;
	onClose: () => void;
	passengerId: string;
	passengerName: string;
}

const overdueDaysOf = (invoice: IInvoiceSummary): number => {
	if (invoice.overdueDays !== undefined) return invoice.overdueDays;
	const msPerDay = 24 * 60 * 60 * 1000;
	return Math.floor((Date.now() - new Date(`${invoice.dueDate}T00:00:00`).getTime()) / msPerDay);
};

export const PassengerAccountStatement = ({
	open,
	onClose,
	passengerId,
	passengerName,
}: PassengerAccountStatementProps): JSX.Element => {
	const auth = useSelector((state: RootState) => state.auth);
	const [invoices, setInvoices] = useState<IInvoiceSummary[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [onlyPending, setOnlyPending] = useState(true);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [summary, setSummary] = useState<IPassengerInvoiceSummary | null>(null);

	const fetchTable = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await getInvoicesByPassenger(passengerId, {
				page,
				size: PAGE_SIZE,
				sort: '-issueDate',
				status: [InvoiceStatusEnum.Issued],
				hasPendingBalance: onlyPending || undefined,
			});
			setInvoices(response.elements);
			setTotal(response.total);
		} catch {
			setError('No se pudo cargar el estado de cuenta.');
		} finally {
			setLoading(false);
		}
	}, [passengerId, page, onlyPending]);

	const fetchSummary = useCallback(async () => {
		try {
			setSummary(await getPassengerInvoiceSummary(passengerId));
		} catch {
			setSummary(null);
		}
	}, [passengerId]);

	useEffect(() => {
		if (!open) return;
		fetchTable();
	}, [open, fetchTable]);

	useEffect(() => {
		if (!open) return;
		fetchSummary();
	}, [open, fetchSummary]);

	useEffect(() => {
		if (open) {
			setPage(1);
			setOnlyPending(true);
		}
	}, [open, passengerId]);

	const pageTotal = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

	const handleOpenSaleOrder = (saleOrderId: string): void => {
		const url = routes.salesOrder.edit.replace(':id', saleOrderId);
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const overdueDaysCell = (invoice: IInvoiceSummary): JSX.Element => {
		const days = overdueDaysOf(invoice);

		if (invoice.pendingAmount <= 0) {
			return (
				<Typography variant="body2" color="text.secondary">
					—
				</Typography>
			);
		}

		if (days > 0) {
			return (
				<Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
					{days} días
				</Typography>
			);
		}

		return (
			<Typography variant="body2" color="text.secondary">
				en plazo ({days})
			</Typography>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<Box>
					Estado de cuenta
					<Typography variant="body2" color="text.secondary">
						{passengerName}
					</Typography>
				</Box>
				<Box display="flex" alignItems="center" gap={1}>
					<Tooltip title="Versión imprimible con logo, para enviar al cliente">
						<Button
							size="small"
							variant="outlined"
							startIcon={<OpenInNewIcon />}
							onClick={() =>
								window.open(
									`${routes.passengers.accountStatement.replace(':id', passengerId)}?agencyId=${getAgencyIdOrDefault(
										auth.country,
									)}`,
									'_blank',
									'noopener,noreferrer',
								)
							}
						>
							Pantalla completa / PDF
						</Button>
					</Tooltip>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent>
				{/* ─ Resumen de saldos ─ */}
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item xs={12} sm={4}>
						<Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
							<Typography variant="caption" color="text.secondary">
								Saldo total adeudado
							</Typography>
							<Typography variant="h6">{summary ? formatAmount(summary.totalPendingBalance, 'USD') : '—'}</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Box sx={{ border: 1, borderColor: 'error.light', borderRadius: 1, p: 1.5 }}>
							<Typography variant="caption" color="text.secondary">
								Vencido {summary && summary.overdueDocumentsCount > 0 ? `(${summary.overdueDocumentsCount} doc.)` : ''}
							</Typography>
							<Typography
								variant="h6"
								sx={{ color: summary && summary.overduePendingBalance > 0 ? 'error.main' : undefined }}
							>
								{summary ? formatAmount(summary.overduePendingBalance, 'USD') : '—'}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
							<Typography variant="caption" color="text.secondary">
								Por vencer
							</Typography>
							<Typography variant="h6">
								{summary ? formatAmount(summary.notYetDuePendingBalance, 'USD') : '—'}
							</Typography>
						</Box>
					</Grid>
				</Grid>

				<Box display="flex" gap={1} sx={{ mb: 1.5 }}>
					<Chip
						label="Solo con saldo"
						size="small"
						color={onlyPending ? 'warning' : 'default'}
						variant={onlyPending ? 'filled' : 'outlined'}
						onClick={() => {
							setPage(1);
							setOnlyPending((v) => !v);
						}}
					/>
				</Box>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				) : invoices.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
						{onlyPending
							? 'El cliente no tiene facturas emitidas con saldo.'
							: 'El cliente no tiene facturas emitidas.'}
					</Typography>
				) : (
					<TableContainer sx={{ overflowX: 'auto' }}>
						<Table size="small" sx={{ minWidth: 820 }}>
							<TableHead>
								<TableRow>
									<TableCell>Emisión</TableCell>
									<TableCell>Documento</TableCell>
									<TableCell>Concepto</TableCell>
									<TableCell>Vencimiento</TableCell>
									<TableCell>Días vencidos</TableCell>
									<TableCell align="right">Importe</TableCell>
									<TableCell align="right">Saldo</TableCell>
									<TableCell align="right">Orden</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{invoices.map((invoice) => {
									const pendingUsd = pendingAmountInMainCurrency(invoice);

									return (
										<TableRow key={invoice.id} hover>
											<TableCell>{formatDate(invoice.issueDate)}</TableCell>
											<TableCell>
												<Tooltip title="Abrir la orden de venta de esta factura">
													<Typography
														variant="body2"
														color="primary"
														sx={{ fontWeight: 600, cursor: 'pointer' }}
														onClick={() => handleOpenSaleOrder(invoice.saleOrderId)}
													>
														{invoice.invoiceNumber}
													</Typography>
												</Tooltip>
												<Typography variant="caption" color="text.secondary">
													{TYPE_LABEL[invoice.type]}
													{invoice.externalReference ? ` · ${invoice.externalReference}` : ''}
												</Typography>
											</TableCell>
											<TableCell sx={{ maxWidth: 280 }}>
												<Typography variant="caption" color="primary" display="block">
													{invoice.dealId ? `#${invoice.dealId}` : '—'}
												</Typography>
												{invoice.notes ? (
													<Tooltip title={invoice.notes}>
														<Typography variant="caption" color="text.secondary" noWrap display="block">
															{invoice.notes}
														</Typography>
													</Tooltip>
												) : null}
											</TableCell>
											<TableCell>{formatDate(invoice.dueDate)}</TableCell>
											<TableCell>{overdueDaysCell(invoice)}</TableCell>
											<TableCell align="right">
												{formatAmount(invoice.total, invoice.currency)}
												{invoice.currency !== 'USD' && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														≈ {formatAmount(invoice.totalInMainCurrency, 'USD')}
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												{invoice.pendingAmount > 0 ? (
													<>
														<Typography
															variant="body2"
															sx={{
																color: overdueDaysOf(invoice) > 0 ? 'error.main' : 'warning.main',
																fontWeight: 600,
															}}
														>
															{formatAmount(invoice.pendingAmount, invoice.currency)}
														</Typography>
														{invoice.currency !== 'USD' && (
															<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
																≈ {formatAmount(pendingUsd, 'USD')}
															</Typography>
														)}
													</>
												) : (
													<Typography variant="body2" color="success.main">
														{formatAmount(0, invoice.currency)}
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												<Tooltip title="Abrir orden de venta">
													<IconButton size="small" onClick={() => handleOpenSaleOrder(invoice.saleOrderId)}>
														<OpenInNewIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{pageTotal > 1 && (
					<Box display="flex" justifyContent="center" mt={2}>
						<Pagination count={pageTotal} page={page} onChange={(_, p) => setPage(p)} color="primary" size="small" />
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default PassengerAccountStatement;
